import { useState } from 'react'
import { Plus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import Card from 'app/frontend/base/components/Card'
import LoginButtons from 'app/frontend/base/components/LoginButtons'
import ClaimExistingCharacterButton from 'app/frontend/base/components/ClaimExistingCharacterButton'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { useGuild } from 'app/frontend/base/config/context/guild'
import { cn } from '@/lib/utils'
import AddOwnCandidateDialog from './AddOwnCandidateDialog'
import CandidateName from './CandidateName'
import CandidateCommentAction from './CandidateCommentAction'
import CandidateContextMenu from './CandidateContextMenu'

type Props = {
  candidates: wowaudit.GuildCandidate[]
}

const CandidateCard = ({
  candidate,
}: {
  candidate: wowaudit.GuildCandidate
}) => (
  <Card
    className={cn(
      'inline-flex items-center justify-between py-1 px-2 gap-1',
      candidate.status === 'rejected' && 'bg-destructive hover:bg-destructive',
    )}
  >
    <CandidateName candidate={candidate} />

    <div className='flex items-center justify-end gap-0.5 shrink-0'>
      {candidate.status === 'rejected' && (
        <Badge
          variant='outline'
          className='font-normal text-destructive-foreground'
        >
          Rejected
        </Badge>
      )}
      <CandidateCommentAction candidate={candidate} />
      <CandidateContextMenu candidate={candidate} />
    </div>
  </Card>
)

const MyCandidacy = ({ candidates }: Props) => {
  const {
    data: { user },
  } = usePageInfo()
  const { guild, guildReference } = useGuild()
  const [dialogOpen, setDialogOpen] = useState(false)
  const guildName = guild?.name ?? guildReference?.name

  if (!user) {
    return (
      <Card className='mb-6 flex flex-col items-center gap-3 py-6 text-center'>
        <p className='text-sm text-muted-foreground'>
          Want to join {guildName}? Log in to add the character you want to join
          with.
        </p>
        <LoginButtons />
      </Card>
    )
  }

  const ownCandidates = candidates.filter(
    (candidate) => candidate.id !== null && candidate.userId === user.id,
  )
  const unclaimedCharacters = candidates
    .filter((candidate) => candidate.userId === null)
    .map((candidate) => candidate.characterReference)

  return (
    <>
      <Card className='mb-6'>
        {ownCandidates.length > 0 ? (
          <>
            <div className='flex items-center justify-between gap-2 mb-3'>
              <h4>Your characters</h4>
              <div className='flex items-center gap-2'>
                <ClaimExistingCharacterButton
                  characters={unclaimedCharacters}
                  className='h-7 px-2 text-xs'
                />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='outline'
                      size='custom'
                      className='h-7 w-7'
                      onClick={() => setDialogOpen(true)}
                    >
                      <Plus size={16} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Add another character</TooltipContent>
                </Tooltip>
              </div>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2'>
              {ownCandidates.map((candidate) => (
                <CandidateCard
                  key={candidate.characterReference.id}
                  candidate={candidate}
                />
              ))}
            </div>
          </>
        ) : (
          <div className='flex flex-col items-center gap-3 py-2 text-center'>
            <p className='text-sm text-muted-foreground'>
              Want to join {guildName}? Add the character you plan to play once
              the game releases.
            </p>
            <div className='flex flex-wrap items-center justify-center gap-2'>
              <Button className='gap-2' onClick={() => setDialogOpen(true)}>
                <Plus size={16} />
                Add your character
              </Button>
              <ClaimExistingCharacterButton
                characters={unclaimedCharacters}
                className='h-10 px-4'
              />
            </div>
          </div>
        )}
      </Card>

      <AddOwnCandidateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        candidates={candidates}
      />
    </>
  )
}

export default MyCandidacy
